import { CancelOutlined, FilterList } from "@material-ui/icons";
import { observer } from "mobx-react-lite";
import React, { useEffect } from "react";
import { ButtonGroup, Form, OverlayTrigger } from "react-bootstrap";
import Select from "react-select";
import styled from "styled-components";
import { useProject } from "../../pages/Workspace/hooks";
import { useStore } from "../../store";
import FormulationFilterModel from "../../store/models/FormulationFilter";
import Button from "../Button";

type Props = {};

const Filters = styled(Form)`
  background-color: #ffffff;
  border: 1px solid #b9b9b9;
  border-radius: 0.25rem;
  box-shadow: 0px 0.25rem 0.75rem rgba(0, 0, 0, 0.1);
  font-size: 0.75rem;
  font-weight: bold;
  padding: 1.25rem;
  width: 18rem;
  z-index: 10;
`;

const FormFooter = styled.footer`
  text-align: right;
`;

const FormulationFilter: React.FC<Props> = () => {
  const store = useStore();
  const project = useProject();

  useEffect(() => {
    if (project) {
      store.formulations.createFilters(project.id);
    }
  }, [project, store.formulations]);

  const filter =
    store.formulations.filters.get(project?.id || 0) ||
    new FormulationFilterModel({});

  const { isFiltering } = filter;

  const productSpecs = project
    ? store.formulations.productSpecsOfProject(project.id)
    : [];
  const viscosities = project
    ? store.formulations.viscositiesOfProject(project.id)
    : [];
  const formulationCodes = project
    ? store.formulations.formulationCodesOfProject(project.id)
    : [];
  const plants = project ? store.formulations.plantsOfProject(project.id) : [];
  const countries = project
    ? store.formulations.countriesOfProject(project.id)
    : [];
  const clusters = project
    ? store.formulations.clustersOfProject(project.id)
    : [];

  return (
    <ButtonGroup>
      <OverlayTrigger
        trigger="click"
        placement="bottom-start"
        offset={[0, 20]}
        rootClose
        overlay={
          <Filters>
            <Form.Group>
              <Form.Label>Product Spec</Form.Label>
              <Select
                options={productSpecs}
                onChange={(value) => {
                  filter.setProductSpecs(value.map((v) => v.value));
                  if (project) {
                    store.formulations.commitCsv(project.id);
                  }
                }}
                value={filter.selectedProductSpecs}
                isMulti
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Formulation Code</Form.Label>
              <Select
                options={formulationCodes}
                onChange={(value) => {
                  filter.setFormulationCodes(value.map((v) => v.value));
                  if (project) {
                    store.formulations.commitCsv(project.id);
                  }
                }}
                value={filter.selectedFormulationCodes}
                isMulti
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Viscosity</Form.Label>
              <Select
                options={viscosities}
                onChange={(value) => {
                  filter.setViscosities(value.map((v) => v.value));
                  if (project) {
                    store.formulations.commitCsv(project.id);
                  }
                }}
                value={filter.selectedViscosities}
                isMulti
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Plant</Form.Label>
              <Select
                options={plants}
                onChange={(value) => {
                  filter.setPlants(value.map((v) => v.value));
                  if (project) {
                    store.formulations.commitCsv(project.id);
                  }
                }}
                value={filter.selectedPlants}
                isMulti
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>CLT</Form.Label>
              <Select
                options={clusters}
                onChange={(value) => {
                  filter.setClusters(value.map((v) => v.value));
                  if (project) {
                    store.formulations.commitCsv(project.id);
                  }
                }}
                value={filter.selectedClusters}
                isMulti
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Countries</Form.Label>
              <Select
                options={countries}
                onChange={(value) => {
                  filter.setCountries(value.map((v) => v.value));
                  if (project) {
                    store.formulations.commitCsv(project.id);
                  }
                }}
                value={filter.selectedCountries}
                isMulti
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Approved for Costing?</Form.Label>
              <Select
                options={[
                  {
                    label: "Yes",
                    value: true,
                  },
                  { label: "No", value: false },
                ]}
                onChange={(value) => {
                  filter.setApproved(value.map((v) => v.value));
                  if (project) {
                    store.formulations.commitCsv(project.id);
                  }
                }}
                value={filter.selectedApproved}
                isMulti
              />
            </Form.Group>
            <FormFooter>
              <Button
                type="button"
                variant="borderless"
                size="thin"
                onClick={() => {
                  filter.reset();
                  if (project) {
                    store.formulations.commitCsv(project.id);
                  }
                }}
              >
                Clear
              </Button>
            </FormFooter>
          </Filters>
        }
      >
        <Button variant={isFiltering ? "inverted" : "primary"} size="thin">
          <FilterList /> Filter
        </Button>
      </OverlayTrigger>
      {isFiltering ? (
        <Button
          variant="inverted"
          size="thin"
          onClick={() => {
            filter.reset();
            if (project) {
              store.formulations.commitCsv(project.id);
            }
          }}
        >
          <CancelOutlined />
          Clear Filter
        </Button>
      ) : null}
    </ButtonGroup>
  );
};

export default observer(FormulationFilter);
